/**
 * Send Run Confirm Modal — replaces the native confirm() in SendRunButton.
 *
 * Two paths from one screen:
 *  - "Send all eligible leads" → dispatch with no file scoping (original
 *    behavior of the run button).
 *  - "Send leads by file" → expand a multi-select dropdown of recent uploads
 *    (cap 30, matching Firestore `in`). On confirm the dispatcher pulls
 *    eligible leads only from the picked files in the chosen run — if those
 *    files don't have enough eligible leads to fill the requested count,
 *    fewer calls are sent (no top-up from other files).
 *
 * Loads the upload list lazily — only when the user expands the by-file
 * panel — so the all-eligible path stays instant.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import { UPLOAD_LIST_WAIT_MS, uploadListErrorMessage } from './sendOutcome';

const MAX_FILES_PER_SEND = 30;
/**
 * Sends this big need the word SEND typed before either send button works (2026-10-06,
 * JAD-SUP-20261006-001: a client sent 2,000 calls with two taps, then asked how to stop them — a send
 * can't be stopped once it is handed to the calling system).
 */
const TYPED_CONFIRM_MIN = 1000;
const TYPED_CONFIRM_WORD = 'SEND';

interface FileOption {
  sourceFile: string;
  /** Pending leads in this file + run that are callable right now. */
  availableInRun: number;
  /** All pending leads in this file + run, including those still in their recall window. */
  totalInRun: number;
  uploadedAt: number | null;
}

interface SendRunConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Confirmed send. `sourceFiles` undefined = send all eligible. `count` = the number shown on the by-file button. */
  onConfirm: (args: { sourceFiles?: string[]; count?: number }) => void;
  runNumber: number;
  count: number;
  clientId?: string | null;
  billingType: 'per_call' | 'per_contact';
  /** Call-credit balance (per_call billing). */
  balance: number;
}

export function SendRunConfirmModal({
  isOpen, onClose, onConfirm, runNumber, count, clientId, billingType, balance,
}: SendRunConfirmModalProps) {
  const { user } = useAuth();
  const [mode, setMode] = useState<'all' | 'file'>('all');
  const [fileOptions, setFileOptions] = useState<FileOption[] | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [typedConfirm, setTypedConfirm] = useState('');

  const targetClientId = clientId || user?.uid || '';

  // Reset state every time the modal is reopened so a previous "by file"
  // selection (or a typed SEND) doesn't bleed into a fresh confirm. The upload
  // list too (Codex 2026-10-30): the modal stays mounted, so its counts could be
  // hours old, and a file still "in recall" stayed disabled after it unlocked.
  useEffect(() => {
    if (isOpen) {
      setMode('all');
      setPicked(new Set());
      setFilesError(null);
      setTypedConfirm('');
      setFileOptions(null);
    }
  }, [isOpen]);

  // Lazy-load uploads only the first time the user expands the by-file
  // panel. Single callable round trip — admin SDK fans out 50× count()
  // queries inside Google's network, returning the full list in ~100 ms
  // instead of 5–30 s of browser→Firestore aggregation hops.
  //
  // Deps deliberately exclude `fileOptions` / `filesLoading` — including
  // them caused the effect to tear itself down when setFilesLoading(true)
  // re-ran the effect, which fired the cleanup and set cancelled=true on
  // the still-in-flight call, swallowing the response forever.
  useEffect(() => {
    if (!isOpen || mode !== 'file' || !targetClientId) return;
    if (fileOptions !== null || filesLoading) return;
    let cancelled = false;
    setFilesLoading(true);
    setFilesError(null);
    const fn = httpsCallable(functions, 'listUploadFileOptions', { timeout: UPLOAD_LIST_WAIT_MS });
    fn({ runNumber, ...(clientId ? { clientId } : {}) })
      .then(resp => {
        if (cancelled) return;
        const payload = resp.data as { files?: FileOption[] };
        setFileOptions(Array.isArray(payload.files) ? payload.files : []);
      })
      .catch(err => {
        if (cancelled) return;
        // Leave fileOptions null so the next expand retries — [] would read as "no uploads".
        setFilesError(uploadListErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setFilesLoading(false);
      });
    // Collapsed or closed mid-call: the modal stays mounted, so clear loading here or the
    // guard above blocks every later expand.
    return () => { cancelled = true; setFilesLoading(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, targetClientId, clientId, runNumber]);

  if (!isOpen) return null;

  const togglePicked = (sf: string, hasAvailable: boolean) => {
    if (!hasAvailable) return;
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(sf)) {
        next.delete(sf);
      } else if (next.size < MAX_FILES_PER_SEND) {
        next.add(sf);
      }
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    if (!fileOptions) return;
    setPicked(new Set(
      fileOptions
        .filter(f => f.availableInRun > 0)
        .slice(0, MAX_FILES_PER_SEND)
        .map(f => f.sourceFile)
    ));
  };
  const handleClearAll = () => setPicked(new Set());

  // Total leads ready right now across the picked files. Caps the send: if
  // selectedAvailable < count, the dispatcher will only send selectedAvailable
  // calls (no top-up from other files — see dispatcher.ts).
  const selectedAvailable = (fileOptions ?? [])
    .filter(f => picked.has(f.sourceFile))
    .reduce((sum, f) => sum + f.availableInRun, 0);
  const effectiveByFileCount = Math.min(count, selectedAvailable);

  const fileLabel = picked.size === 0 ? '' : picked.size === 1 ? '1 file' : `${picked.size} files`;
  // Big sends are locked until SEND is typed — both buttons, so the by-file path can't skip it.
  const needsTypedConfirm = count >= TYPED_CONFIRM_MIN;
  const typedOk = !needsTypedConfirm || typedConfirm.trim() === TYPED_CONFIRM_WORD;
  const fileButtonDisabled = !typedOk || (mode === 'file' && (picked.size === 0 || selectedAvailable === 0));
  const overCap = picked.size >= MAX_FILES_PER_SEND;

  const handleSendAll = () => { if (typedOk) onConfirm({}); };
  // The number on the button is the most that gets sent (Codex 2026-10-30): more leads can become eligible after the
  // list loaded, and the request used to carry the Run's whole count.
  const handleSendByFile = () => {
    if (!fileButtonDisabled) onConfirm({ sourceFiles: Array.from(picked), count: effectiveByFileCount });
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--dark-2)', borderRadius: '12px', padding: '28px',
          maxWidth: '520px', width: '92%', border: '1px solid var(--dark-3)',
          maxHeight: '85vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 8px', color: 'var(--white)', fontSize: '1.15rem' }}>
          Send {count.toLocaleString()} call{count !== 1 ? 's' : ''} for Run {runNumber}?
        </h2>
        {/* What this send costs, and that it can't be undone. */}
        <div
          style={{
            border: '1px solid rgba(200,168,78,0.45)', background: 'rgba(200,168,78,0.08)',
            borderRadius: '8px', padding: '10px 12px', marginBottom: '14px',
            color: 'var(--gray-300)', fontSize: '0.85rem', lineHeight: 1.5,
          }}
        >
          {billingType === 'per_call' ? (
            <>
              Up to <strong>{count.toLocaleString()}</strong> call{count !== 1 ? 's' : ''} start dialing within a minute.
              Each call sent uses 1 call credit — you have {balance.toLocaleString()}.
            </>
          ) : (
            <>
              Up to <strong>{count.toLocaleString()}</strong> call{count !== 1 ? 's' : ''} start dialing within a minute.
              Contact credits are used for calls that reach the prospect.
            </>
          )}
          <br />
          <strong style={{ color: 'var(--gold)' }}>Once sent, calls can't be stopped or pulled back.</strong>
        </div>

        {needsTypedConfirm && (
          <div style={{ marginBottom: '14px' }}>
            <label
              htmlFor="send-run-typed-confirm"
              style={{ display: 'block', color: 'var(--gray-300)', fontSize: '0.85rem', marginBottom: '6px' }}
            >
              This is a big send. Type <strong>{TYPED_CONFIRM_WORD}</strong> to confirm:
            </label>
            <input
              id="send-run-typed-confirm"
              type="text"
              value={typedConfirm}
              onChange={e => setTypedConfirm(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder={TYPED_CONFIRM_WORD}
              style={{
                width: '100%', padding: '10px', borderRadius: '6px', boxSizing: 'border-box',
                border: '1px solid var(--dark-3)', background: 'var(--dark)', color: 'var(--white)',
                fontSize: '0.95rem', letterSpacing: '0.08em',
              }}
            />
          </div>
        )}

        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '16px' }}>
          Choose how to pick which leads get called.
        </p>

        {/* All eligible — primary path (typed SEND first on big sends) */}
        <button
          onClick={handleSendAll}
          disabled={!typedOk}
          className="btn btn-primary"
          style={{ width: '100%', padding: '12px', marginBottom: '10px' }}
        >
          Send all eligible leads
        </button>

        {/* By file — expandable */}
        <div style={{ border: '1px solid var(--dark-3)', borderRadius: '8px', padding: '12px', background: 'var(--dark)' }}>
          <button
            onClick={() => setMode(m => m === 'file' ? 'all' : 'file')}
            className="btn btn-secondary"
            style={{ width: '100%', padding: '10px', textAlign: 'left' }}
          >
            <span style={{ fontWeight: 600 }}>
              {mode === 'file' ? '▾' : '▸'} Send leads by file
            </span>
            {fileLabel && (
              <span style={{ marginLeft: '8px', color: 'var(--gold)', fontSize: '0.85rem' }}>
                ({fileLabel} selected)
              </span>
            )}
          </button>

          {mode === 'file' && (
            <div style={{ marginTop: '12px' }}>
              {filesLoading && (
                <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Loading uploads…</p>
              )}
              {!filesLoading && filesError && (
                <p style={{ color: '#dc2626', fontSize: '0.85rem' }}>{filesError}</p>
              )}
              {!filesLoading && fileOptions && fileOptions.length === 0 && !filesError && (
                <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                  No labelled uploads with pending leads.
                </p>
              )}
              {!filesLoading && fileOptions && fileOptions.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.75rem' }}>
                    <div style={{ color: 'var(--gray-500)' }}>
                      Pick up to {MAX_FILES_PER_SEND} files. Counts show ready / total in Run {runNumber}.
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={handleSelectAllVisible}
                        className="btn btn-secondary"
                        style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                      >
                        Select all
                      </button>
                      <button
                        onClick={handleClearAll}
                        className="btn btn-secondary"
                        style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      maxHeight: '260px', overflowY: 'auto',
                      border: '1px solid var(--dark-3)', borderRadius: '6px',
                      padding: '4px', background: 'var(--dark-2)',
                    }}
                  >
                    {fileOptions.map(opt => {
                      const isPicked = picked.has(opt.sourceFile);
                      const noneAvailable = opt.availableInRun === 0;
                      const disabled = noneAvailable || (!isPicked && overCap);
                      return (
                        <label
                          key={opt.sourceFile}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '8px 10px', borderRadius: '4px',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.5 : 1,
                            background: isPicked ? 'rgba(200,168,78,0.08)' : 'transparent',
                          }}
                          title={noneAvailable
                            ? `All ${opt.totalInRun.toLocaleString()} pending leads in this file are still inside their recall window for Run ${runNumber}.`
                            : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={isPicked}
                            disabled={disabled}
                            onChange={() => togglePicked(opt.sourceFile, !noneAvailable)}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                color: 'var(--white)', fontSize: '0.85rem',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}
                              title={opt.sourceFile}
                            >
                              {opt.sourceFile}
                            </div>
                            <div style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                              {opt.availableInRun.toLocaleString()} ready
                              {' / '}
                              {opt.totalInRun.toLocaleString()} in Run {runNumber}
                              {noneAvailable && (
                                <span style={{ color: 'var(--gray-500)', marginLeft: '6px' }}>
                                  · in recall
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {overCap && (
                    <p style={{ color: 'var(--gold)', fontSize: '0.75rem', marginTop: '8px' }}>
                      Reached the {MAX_FILES_PER_SEND}-file cap — uncheck one to swap.
                    </p>
                  )}
                  <button
                    onClick={handleSendByFile}
                    disabled={fileButtonDisabled}
                    className="btn btn-primary"
                    style={{ width: '100%', padding: '10px', marginTop: '10px' }}
                  >
                    {picked.size === 0
                      ? `Send ${count.toLocaleString()} from selected file(s)`
                      : `Send ${effectiveByFileCount.toLocaleString()} from ${fileLabel}`}
                  </button>
                  {picked.size > 0 && selectedAvailable < count && (
                    <p style={{ color: 'var(--gold)', fontSize: '0.7rem', marginTop: '6px', lineHeight: 1.4 }}>
                      Selected file{picked.size === 1 ? '' : 's'} only have {selectedAvailable.toLocaleString()} lead{selectedAvailable === 1 ? '' : 's'} ready in Run {runNumber}.
                      Only that many calls will be sent — the {(count - selectedAvailable).toLocaleString()}-lead gap is not filled from other files.
                    </p>
                  )}
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.7rem', marginTop: '6px', lineHeight: 1.4 }}>
                    Only leads from the selected files will be called. If they have fewer than {count.toLocaleString()} eligible leads in Run {runNumber}, fewer calls are sent.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="btn btn-secondary"
          style={{ width: '100%', marginTop: '14px' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
